import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    Backdrop,
    CircularProgress
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AgGridReact } from "ag-grid-react";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";

import { myApi } from "../../config/axiosConfig";
import BtnAcciones from "../../components/plantilla/tabla/BtnAcciones";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import FuelPurchaseModal from "./modales/FuelPurchaseModal";

import Cookies from "js-cookie";

import { useLocation } from "react-router-dom";

import {
    ModuleRegistry,
    AllCommunityModule
} from "ag-grid-community";

ModuleRegistry.registerModules([
    AllCommunityModule
]);

const LOCATIONS = [
    { value: "COLOMBIA", label: "Colombia" },
    { value: "MONTERREY", label: "Monterrey" },
    { value: "ARGUINDEGUI", label: "Arguindegui" }
];

const FUEL_TYPES = [
    { value: "TODOS", label: "Todos" },
    { value: "RED", label: "Diesel rojo" },
    { value: "CLEAR", label: "Diesel claro" }
];

const n2 = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const moneyMXN = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const moneyUSD = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const TankLevel = ({
    liters = 0,
    capacity = 2000000,
    title = "Nivel Diesel",
    unit = "L"
}) => {

    const safeCapacity = Number(capacity) || 1;
    const safeLiters = Math.max(Number(liters) || 0, 0);
    const percent = Math.min(
        Math.max((safeLiters / safeCapacity) * 100, 0),
        100
    );

    const tankColor =
    percent >= 95
        ? {
            liquid: "linear-gradient(90deg, #8b0000 0%, #ff2b2b 50%, #8b0000 100%)",
            text: "CRÍTICO"
        }
        : percent >= 90
            ? {
                liquid: "linear-gradient(90deg, #b45309 0%, #f97316 50%, #b45309 100%)",
                text: "ALTO"
            }
            : percent >= 80
                ? {
                    liquid: "linear-gradient(90deg, #a16207 0%, #facc15 50%, #a16207 100%)",
                    text: "PRECAUCIÓN"
                }
                : {
                    liquid: "linear-gradient(90deg, #1f6b16 0%, #35a12a 50%, #1f6b16 100%)",
                    text: "NORMAL"
                };

    return (
        <Box sx={{ width: 190, textAlign: "center" }}>
            <Typography fontWeight={800} fontSize={14} mb={1}>
                {title}
            </Typography>

            <Box
                sx={{
                    width: 80,
                    height: 110,
                    mx: "auto",
                    position: "relative",
                    borderRadius: "52px / 18px",
                    overflow: "hidden",
                    border: "2px solid #9fb3bd",
                    background: "linear-gradient(90deg, #dce8ee 0%, #f7fbfd 45%, #b9cbd4 100%)",
                    boxShadow: "inset -12px 0 20px rgba(0,0,0,.15)",
                }}
            >
                {/* tapa superior */}
                <Box
                    sx={{
                        position: "absolute",
                        top: -1,
                        left: -2,
                        width: "calc(100% + 4px)",
                        height: 28,
                        borderRadius: "50%",
                        border: "2px solid #9fb3bd",
                        background: "linear-gradient(90deg, #d8e5eb, #ffffff, #b7c8d1)",
                        zIndex: 3,
                    }}
                />

                {/* nivel */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        height: `${percent}%`,
                        //background: "linear-gradient(90deg, #1f6b16 0%, #35a12a 50%, #1f6b16 100%)",
                        background: tankColor.liquid,
                        transition: "height .4s ease",
                        zIndex: 1,
                    }}
                />

                {/* curva del diesel */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: `calc(${percent}% - 13px)`,
                        left: 0,
                        width: "100%",
                        height: 26,
                        borderRadius: "50%",
                        //background: "linear-gradient(90deg, #2b8421, #48b83c, #2b8421)",
                        background: tankColor.liquid,
                        display: percent > 0 ? "block" : "none",
                        zIndex: 2,
                    }}
                />

                {/* brillo */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 28,
                        left: 18,
                        width: 12,
                        height: 135,
                        borderRadius: 8,
                        background: "rgba(255,255,255,.45)",
                        zIndex: 4,
                    }}
                />

                <Typography
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        fontSize: 15,
                        color: "#fff",
                        textShadow: "1px 1px 3px #000",
                        zIndex: 5,
                    }}
                >
                    {safeLiters.toLocaleString("en-US", {
                        maximumFractionDigits: 0,
                    })} {unit}
                </Typography>
            </Box>

            <Typography fontWeight={800} fontSize={13} mt={1}>
                {percent.toFixed(1)}%
            </Typography>
            <Typography fontWeight={800} fontSize={11}>
                {tankColor.text}
            </Typography>
        </Box>
    );
};

const FuelPurchases = () => {

    const routeLocation = useLocation();

    const gridRef = useRef();

    const [data, setData] = useState([]);
    //const [location, setLocation] = useState("");
    const [location, setLocation] = useState(
        routeLocation.state?.location || "COLOMBIA"
    );
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const [openPdf, setOpenPdf] = useState(false);
    const [fileUrl, setFileUrl] = useState("");
    const [tituloPdf, setTituloPdf] = useState("");

    const [pdfKey, setPdfKey] = useState(0);

    const [pdfLoading, setPdfLoading] = useState(false);

    //const [fuelType, setFuelType] = useState("TODOS");
    const [fuelType, setFuelType] = useState(
        routeLocation.state?.fuelType || "TODOS"
    );

    const [tankLevel, setTankLevel] = useState(0);
    const [tankCapacity, setTankCapacity] = useState(0);

    const [arguiTanks, setArguiTanks] = useState({
        RED: { level: 0, capacity: 0 },
        CLEAR: { level: 0, capacity: 0 }
    });

    const [capacityLoading, setCapacityLoading] = useState(false);
    const [tankLoading, setTankLoading] = useState(false);

    const fetchTankLevel = useCallback(async () => 
    {
        if (!location) {
            setTankLevel(0);
            setArguiTanks({
                RED: { level: 0, capacity: 0 },
                CLEAR: { level: 0, capacity: 0 }
            });
            return;
        }

        try {
            setTankLoading(true);

            if (location === "ARGUINDEGUI") {
                const [redLevel, clearLevel, redCapacity, clearCapacity] = await Promise.all([
                    myApi.post("/cargasFuel/getTankLevel", { location, fuelType: "RED" }),
                    myApi.post("/cargasFuel/getTankLevel", { location, fuelType: "CLEAR" }),
                    myApi.post("/cargasFuel/getTankCapacity", { location, fuelType: "RED" }),
                    myApi.post("/cargasFuel/getTankCapacity", { location, fuelType: "CLEAR" })
                ]);

                setArguiTanks({
                    RED: {
                        level: Number(redLevel.data?.level) || 0,
                        capacity: Number(redCapacity.data?.capacity) || 0
                    },
                    CLEAR: {
                        level: Number(clearLevel.data?.level) || 0,
                        capacity: Number(clearCapacity.data?.capacity) || 0
                    }
                });

                return;
            }

            const response = await myApi.post("/cargasFuel/getTankLevel", {
                location
            });

            setTankLevel(response.data.success ? Number(response.data.level) || 0 : 0);

        } catch (error) {
            console.error("Error getTankLevel:", error);
            setTankLevel(0);
        } finally {
            setTankLoading(false);
        }
    }, [location]);

    const fetchTankCapacity = useCallback(async () => {
        if (!location) {
            setTankCapacity(0);
            return;
        }

        if (location === "ARGUINDEGUI") 
        {
            return;
        }

        try {

            const response = await myApi.post(
                "/cargasFuel/getTankCapacity",
                {
                    location
                }
            );

            if (response.data.success) {
                setTankCapacity(
                    Number(response.data.capacity) || 0
                );
            }
            else {
                setTankCapacity(0);
            }

        } catch (error) {

            console.error(
                "Error getTankCapacity:",
                error
            );

            setTankCapacity(0);
        }
    }, [location]);

    const handleSaveTankCapacity = async () => {
        try {

            setCapacityLoading(true);

            const response = await myApi.post(
                "/cargasFuel/updateTankCapacity",
                {
                    location,
                    capacity: Number(tankCapacity) || 0,
                    usuario: Cookies.get('ebt_nombre')
                }
            );

            if (!response.data.success)
            {
                Swal.fire({
                    icon: "error",
                    title: "No se pudo guardar"
                });
            }

        } catch (error) {

            console.error(
                "Error updateTankCapacity:",
                error
            );

        } finally {

            setCapacityLoading(false);

        }
    };

    const handleSaveArguiTankCapacity = async (fuelTypeValue) => {
        try {
            setCapacityLoading(true);

            const response = await myApi.post(
                "/cargasFuel/updateTankCapacity",
                {
                    location: "ARGUINDEGUI",
                    fuelType: fuelTypeValue,
                    capacity: Number(arguiTanks[fuelTypeValue]?.capacity) || 0,
                    usuario: Cookies.get('ebt_nombre')
                }
            );

            if (!response.data.success) {
                Swal.fire({
                    icon: "error",
                    title: "No se pudo guardar"
                });
            }

            await fetchTankLevel();

        } catch (error) {
            console.error("Error updateTankCapacity Arguin:", error);
        } finally {
            setCapacityLoading(false);
        }
    };

    const handleOpenPdf = async (filePath, titulo) => 
    {
        try {

            setPdfLoading(true);

            setOpenPdf(false);
            setFileUrl("");
            setTituloPdf(titulo);

            const response = await myApi.get(
                `/cargasFuel/viewPurchasePdf`,
                {
                    params: {
                        file: filePath,
                        t: Date.now()
                    },
                    responseType: "blob"
                }
            );

            const blob = new Blob([response.data], {
                type: "application/pdf"
            });

            const url = URL.createObjectURL(blob);

            setPdfKey(Date.now());
            setFileUrl(url);
            setOpenPdf(true);

        } catch (error) {

            console.error("Error al abrir PDF:", error);

            Swal.fire({
                icon: "error",
                title: "No se pudo abrir el PDF"
            });

        } finally {

            setPdfLoading(false);

        }
    };

    const formatDate = (date) => {
        if (!date) return "";
        return dayjs(date).format("MM/DD/YY HH:mm:ss");
    };

    const handleOpenModal = (row = null) => {
        setSelected(row);
        setIsEditing(!!row);
        setModalOpen(true);
    };

    const fetchData = useCallback(async () => {

        if (!location) {
            setData([]);
            return;
        }

        setLoading(true);

        try {
            const payload = {
                location,
                fuelType: location === "ARGUINDEGUI" ? fuelType : null,
                fi: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
                ff: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null
            };

            const response = await myApi.post("/cargasFuel/getFuelPurchases", payload);

            if (response.data.success) {
                setData(Array.isArray(response.data.data) ? response.data.data : []);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error getFuelPurchases:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "No se pudieron consultar las compras de diesel"
            });
        } finally {
            setLoading(false);
        }
    }, [location, fuelType, startDate, endDate]);

    const handleDelete = useCallback(async (id) => {
        const result = await Swal.fire({
            title: "¿Eliminar compra?",
            text: "Esta acción no se puede deshacer.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "green",
            cancelButtonColor: "red"
        });

        if (!result.isConfirmed) return;

        try {
            const response = await myApi.delete(`/cargasFuel/deleteFuelPurchase/${id}`);

            if (response.data.success) {

                await fetchData();
                await fetchTankLevel();

                Swal.fire({
                    icon: "success",
                    title: "Compra eliminada"
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error al eliminar"
                });
            }
        } catch (error) {
            console.error("Error deleteFuelPurchase:", error);
        }
    }, [fetchData, fetchTankLevel]);

    const handleCreate = useCallback(async () => {
        await fetchData();
        await fetchTankLevel();
    }, [fetchData, fetchTankLevel]);

    const handleUpdate = useCallback(async () => {
        await fetchData();
        await fetchTankLevel();
    }, [fetchData, fetchTankLevel]);

    const totalsRow = useMemo(() => {
        const sum = (field) =>
            data.reduce((acc, row) => acc + (Number(row[field]) || 0), 0);

       return {
            id: "totals",
            isTotal: true,
            date: "",
            location: "TOTAL",
            liters: sum("liters"),
            gallons: sum("gallons"),
            subtotal: sum("subtotal"),
            iva: sum("iva"),
            total: sum("total")
        };
    }, [data]);

    const handleUploadPdf = useCallback(async (row, tipo, file) => 
    {
        if (!file) return;

        const formData = new FormData();
        formData.append("pdf", file);
        formData.append("tipo", tipo);

        try {

            setPdfLoading(true);

            const response = await myApi.post(
                `/cargasFuel/uploadPurchasePdf/${row.id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            if (response.data.success) {

                handleUpdate(response.data.data);

                Swal.fire({
                    icon: "success",
                    title: "PDF guardado"
                });

            } else {

                Swal.fire({
                    icon: "error",
                    title: "No se pudo guardar el PDF"
                });

            }

        } catch (error) {

            console.error("Error upload PDF:", error);

            Swal.fire({
                icon: "error",
                title: "Error al subir PDF"
            });

        } finally {

            setPdfLoading(false);

        }
    }, [handleUpdate]);

    const handleDeletePdf = useCallback(async (row, tipo) => 
    {
        const result = await Swal.fire({
            title: "¿Eliminar PDF?",
            text: "Se eliminará el archivo de este registro.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "green",
            cancelButtonColor: "red"
        });

        if (!result.isConfirmed) return;

        try {

            setPdfLoading(true);

            const response = await myApi.delete(
                `/cargasFuel/deletePurchasePdf/${row.id}/${tipo}`
            );

            if (response.data.success) {

                handleUpdate(response.data.data);

                Swal.fire({
                    icon: "success",
                    title: "PDF eliminado"
                });

            } else {

                Swal.fire({
                    icon: "error",
                    title: "No se pudo eliminar el PDF"
                });

            }

        } catch (error) {

            console.error("Error delete PDF:", error);

            Swal.fire({
                icon: "error",
                title: "Error al eliminar PDF"
            });

        } finally {

            setPdfLoading(false);

        }
    }, [handleUpdate]);

    const columns = useMemo(() => [
        {
            field: "date",
            headerName: "FECHA",
            pinned: true,
            minWidth: 160,
            cellStyle: { textAlign: "center" },
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: "location",
            headerName: "UBICACIÓN",
            pinned: true,
            minWidth: 150,
            cellStyle: params => ({
                textAlign: "center",
                fontWeight: params.data?.isTotal ? "bold" : "normal"
            })
        },
        ...(location === "ARGUINDEGUI"
            ? [
                {
                    field: "fuel_type",
                    headerName: "TIPO DIESEL",
                    minWidth: 150,
                    cellStyle: { textAlign: "center", fontWeight: "bold" },
                    valueFormatter: (params) => {
                        if (params.value === "RED") return "Diesel rojo";
                        if (params.value === "CLEAR") return "Diesel claro";
                        return "";
                    }
                }
            ]
            : []
        ),
        ...(location === "ARGUINDEGUI"
        ? [
            {
                field: "gallons",
                headerName: "GALONES",
                minWidth: 120,
                cellStyle: { textAlign: "right" },
                valueFormatter: params => n2.format(Number(params.value) || 0)
            },
            {
                field: "subtotal",
                headerName: "MONTO USD",
                minWidth: 140,
                cellStyle: { textAlign: "right" },
                valueFormatter: params => moneyUSD.format(Number(params.value) || 0)
            }
        ]
        : 
        [
            {
                field: "liters",
                headerName: "LITROS",
                minWidth: 120,
                cellStyle: { textAlign: "right" },
                valueFormatter: params => n2.format(Number(params.value) || 0)
            },
            {
                field: "subtotal",
                headerName: "MONTO SIN IVA",
                minWidth: 150,
                cellStyle: { textAlign: "right" },
                valueFormatter: params => moneyMXN.format(Number(params.value) || 0)
            },
            {
                field: "iva",
                headerName: "IVA",
                minWidth: 130,
                cellStyle: { textAlign: "right" },
                valueFormatter: params => moneyMXN.format(Number(params.value) || 0)
            },
            {
                field: "total",
                headerName: "TOTAL",
                minWidth: 140,
                cellStyle: { textAlign: "right" },
                valueFormatter: params => moneyMXN.format(Number(params.value) || 0)
            }
        ]
        ),
        {
            field: "unit_price",
            headerName:
                location === "ARGUINDEGUI"
                    ? "PRECIO / GALÓN"
                    : "PRECIO / LITRO",
            minWidth: 150,
            cellStyle: { textAlign: "right" },
            valueGetter: (params) => {


                if (location === "ARGUINDEGUI") {

                    const subtotal = Number(params.data?.subtotal) || 0;
                    const gallons = Number(params.data?.gallons) || 0;

                    return gallons > 0
                        ? subtotal / gallons
                        : 0;
                }

                const total = Number(params.data?.total) || 0;
                const liters = Number(params.data?.liters) || 0;

                return liters > 0
                    ? total / liters
                    : 0;
            },
            valueFormatter: (params) => {

                const value = Number(params.value) || 0;

                return location === "ARGUINDEGUI"
                    ? `${moneyUSD.format(value)}/GAL`
                    : `${moneyMXN.format(value)}/L`;
            }
        },
        {
            field: "supplier",
            headerName: "PROVEEDOR",
            minWidth: 170,
            cellStyle: { textAlign: "center" }
        },
        {
            field: "invoice",
            headerName: "FACTURA / ORDEN",
            minWidth: 150,
            cellStyle: { textAlign: "center" }
        },
        {
            field: "comments",
            headerName: "COMENTARIOS",
            minWidth: 220,
            cellStyle: { textAlign: "left" }
        },
        {
            headerName: "PDF ORDEN",
            field: "pdf_delivery_order",
            pinned: "right",
            width: 85,
            minWidth: 85,
            maxWidth: 85,
            sortable: false,
            filter: false,
            suppressMenu: true,
            resizable: false,
            cellStyle: {
                textAlign: "center",
                padding: 0
            },
            cellRenderer: (params) => {
                if (params.data?.isTotal) return null;

                const hasPdf = !!params.data?.pdf_delivery_order;

                return (
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                        {!hasPdf ? (
                            <IconButton component="label" color="primary">
                                <UploadFileIcon />

                                <input
                                    hidden
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) =>
                                        handleUploadPdf(
                                            params.data,
                                            "delivery_order",
                                            e.target.files?.[0]
                                        )
                                    }
                                />
                            </IconButton>
                        ) : (
                            <>
                                <IconButton
                                    color="error"
                                    onClick={() =>
                                        handleOpenPdf(
                                            params.data.pdf_delivery_order,
                                            "PDF ORDEN DE ENTREGA"
                                        )
                                    }
                                >
                                    <PictureAsPdfIcon />
                                </IconButton>

                                <IconButton
                                    color="error"
                                    onClick={() =>
                                        handleDeletePdf(params.data, "delivery_order")
                                    }
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </>
                        )}
                    </Box>
                );
            }
        },
        {
            headerName: "PDF FACTURA",
            field: "pdf_invoice",
            pinned: "right",
            width: 95,
            minWidth: 95,
            maxWidth: 95,
            sortable: false,
            filter: false,
            suppressMenu: true,
            resizable: false,
            cellStyle: {
                textAlign: "center",
                padding: 0
            },
            cellRenderer: (params) => {
                if (params.data?.isTotal) return null;

                const hasPdf = !!params.data?.pdf_invoice;

                return (
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                        {!hasPdf ? (
                            <IconButton component="label" color="primary">
                                <UploadFileIcon />

                                <input
                                    hidden
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) =>
                                        handleUploadPdf(
                                            params.data,
                                            "invoice",
                                            e.target.files?.[0]
                                        )
                                    }
                                />
                            </IconButton>
                        ) : (
                            <>
                                <IconButton
                                    color="error"
                                    onClick={() =>
                                        handleOpenPdf(
                                            params.data.pdf_invoice,
                                            "PDF FACTURA"
                                        )
                                    }
                                >
                                    <PictureAsPdfIcon />
                                </IconButton>

                                <IconButton
                                    color="error"
                                    onClick={() =>
                                        handleDeletePdf(params.data, "invoice")
                                    }
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </>
                        )}
                    </Box>
                );
            }
        },
        {
            headerName: "ACCIONES",
            pinned: "right",
            field: "actions",
            width: 105,
            minWidth: 105,
            maxWidth: 105,
            sortable: false,
            filter: false,
            suppressMenu: true,
            resizable: false,
            cellRenderer: (params) => {
                if (params.data?.isTotal) return null;

                return (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <BtnAcciones
                            icon={<BorderColorIcon />}
                            label="Editar"
                            onClick={() => handleOpenModal(params.data)}
                        />

                        <BtnAcciones
                            icon={<DeleteIcon />}
                            label="Eliminar"
                            onClick={() => handleDelete(params.data.id)}
                        />
                    </Box>
                );
            }
        }
    ], [handleDelete, location, handleUploadPdf, handleDeletePdf]);

    const handleExport = () => {
        gridRef.current.api.exportDataAsCsv({
            fileName: `compras_diesel_${location}.csv`,
            columnKeys: location === "ARGUINDEGUI"
            ? [
                "date",
                "location",
                "fuel_type",
                "gallons",
                "subtotal",
                "supplier",
                "invoice",
                "comments"
            ]
            : [
                "date",
                "location",
                "liters",
                "subtotal",
                "iva",
                "total",
                "supplier",
                "invoice",
                "comments"
            ]
        });
    };

    useEffect(() => {
        if (!location) return;

        fetchData();
        fetchTankLevel();
        fetchTankCapacity();

    }, [
        location,
        fuelType,
        startDate,
        endDate,
        fetchData,
        fetchTankLevel,
        fetchTankCapacity
    ]);

    return (
        
        <>
            <Backdrop
                open={pdfLoading}
                sx={{
                    color: "#fff",
                    zIndex: 999999
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Paper
                sx={{
                    p: 2,
                    height: "calc(100vh - 160px)",
                    minHeight: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    boxSizing: "border-box"
                }}
            >
                <Box sx={{ flexShrink: 0 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: location ? 8 : 12 }}>
                            <Typography variant="h4" fontWeight={800}>
                                Compras de Diesel
                            </Typography>
                        </Grid>

                        {location && (
                        <Grid
                            size={{ xs: 12 }}
                            sx={{
                                display: "flex",
                                justifyContent: location ? "flex-start" : "center",
                                mt: location ? 0 : 8
                            }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddCircleIcon />}
                                onClick={() => handleOpenModal()}
                                sx={{
                                    bgcolor: "black",
                                    color: "white",
                                    fontWeight: "bold",
                                    "&:hover": { bgcolor: "#222" }
                                }}
                            >
                                Agregar
                            </Button>
                        </Grid>
                        )}

                        <Grid
                            size={{ xs: 12, sm: location ? 3 : 4 }}
                            sx={{
                                mx: location ? 0 : "auto",
                                mt: location ? 0 : 8
                            }}
                        >
                            <FormControl fullWidth>
                                <InputLabel>Ubicación</InputLabel>
                                <Select
                                    value={location}
                                    label="Ubicación"
                                    onChange={(e) => setLocation(e.target.value)}
                                >
                                    {LOCATIONS.map(item => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {location === "ARGUINDEGUI" && (
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Combustible</InputLabel>
                                    <Select
                                        value={fuelType}
                                        label="Combustible"
                                        onChange={(e) => setFuelType(e.target.value)}
                                    >
                                        {FUEL_TYPES.map(item => (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {location && (
                        <>

                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Fecha inicial"
                                            value={startDate}
                                            onChange={setStartDate}
                                            format="MM/DD/YY"
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>

                                    <IconButton onClick={() => setStartDate(null)}>
                                        <ClearIcon />
                                    </IconButton>
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Fecha final"
                                            value={endDate}
                                            onChange={setEndDate}
                                            format="MM/DD/YY"
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>

                                    <IconButton onClick={() => setEndDate(null)}>
                                        <ClearIcon />
                                    </IconButton>
                                </Box>
                            </Grid>

                            {/* <Grid size={{ xs: 12, sm: 3 }}>
                                <Button
                                    variant="contained"
                                    color="inherit"
                                    startIcon={<ManageSearchIcon />}
                                    onClick={fetchData}
                                    sx={{ fontWeight: "bold", height: 54 }}
                                >
                                    Buscar
                                </Button>
                            </Grid> */}

                        </>
                        )}
                        </Grid>
                </Box>
                
                {location && (
                <Box
                    sx={{
                        mt: 2,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden"
                    }}
                >

                    <Box sx={{ mb: 1 }}>
                        <Button variant="contained" onClick={handleExport}>
                            Exportar a Excel
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            gap: 2,
                            overflow: "hidden",
                            alignItems: "stretch"
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden"
                            }}
                        >
                            

                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                <div
                                    className="ag-theme-quartz"
                                    style={{
                                        height: "100%",
                                        width: "100%"
                                    }}
                                >
                                    <AgGridReact
                                        ref={gridRef}
                                        rowData={data}
                                        columnDefs={columns}
                                        loading={loading}
                                        pagination
                                        paginationPageSize={100}
                                        pinnedBottomRowData={[totalsRow]}
                                        getRowId={(params) => String(params.data.id)}
                                        defaultColDef={{
                                            sortable: true,
                                            filter: true,
                                            resizable: true
                                        }}
                                    />
                                </div>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width: location === "ARGUINDEGUI" ? 260 : 220,
                                flexShrink: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                pt: 1,
                                gap: 2
                            }}
                        >
                            {tankLoading ? (
                                <CircularProgress />
                            ) : location === "ARGUINDEGUI" ? (
                                <>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Capacidad rojo"
                                        type="number"
                                        value={arguiTanks.RED.capacity}
                                        disabled={capacityLoading}
                                        onChange={(e) =>
                                            setArguiTanks(prev => ({
                                                ...prev,
                                                RED: {
                                                    ...prev.RED,
                                                    capacity: e.target.value
                                                }
                                            }))
                                        }
                                        onBlur={() => handleSaveArguiTankCapacity("RED")}
                                    />

                                    <TankLevel
                                        liters={arguiTanks.RED.level}
                                        capacity={arguiTanks.RED.capacity || 1}
                                        title="Diesel rojo (Termos)"
                                        unit="GAL"
                                    />

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Capacidad claro"
                                        type="number"
                                        value={arguiTanks.CLEAR.capacity}
                                        disabled={capacityLoading}
                                        onChange={(e) =>
                                            setArguiTanks(prev => ({
                                                ...prev,
                                                CLEAR: {
                                                    ...prev.CLEAR,
                                                    capacity: e.target.value
                                                }
                                            }))
                                        }
                                        onBlur={() => handleSaveArguiTankCapacity("CLEAR")}
                                    />

                                    <TankLevel
                                        liters={arguiTanks.CLEAR.level}
                                        capacity={arguiTanks.CLEAR.capacity || 1}
                                        title="Diesel claro (Tractos)"
                                        unit="GAL"
                                    />
                                </>
                            ) : (
                                <>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Capacidad tanque"
                                        type="number"
                                        value={tankCapacity}
                                        disabled={capacityLoading}
                                        onChange={(e) => setTankCapacity(e.target.value)}
                                        onBlur={handleSaveTankCapacity}
                                    />

                                    <TankLevel
                                        liters={tankLevel}
                                        capacity={tankCapacity || 1}
                                        title={`Nivel Diesel - ${location}`}
                                        unit="L"
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
                )}
            </Paper>
            <Dialog
                open={openPdf}
                onClose={() => {
                    if (fileUrl) {
                        URL.revokeObjectURL(fileUrl);
                    }

                    setOpenPdf(false);
                    setFileUrl("");
                }}
                maxWidth={false}
                sx={{
                    "& .MuiDialog-paper": {
                        width: "70vw",
                        height: "80vh",
                        maxWidth: "none"
                    },
                    "& .MuiDialogContent-root": {
                        padding: 0,
                        height: "100%"
                    }
                }}
            >
                <DialogTitle>
                    {tituloPdf}
                </DialogTitle>

                <DialogContent>
                    {fileUrl && (
                        <iframe
                            key={pdfKey}
                            src={fileUrl}
                            title={tituloPdf}
                            style={{
                                width: "100%",
                                height: "100%",
                                border: "none"
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <FuelPurchaseModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                carga={selected}
                isEditing={isEditing}
                defaultLocation={location}
                tankLevel={tankLevel}
                tankCapacity={tankCapacity}
                arguiTanks={arguiTanks}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
            />
        </>
    );
};

export default FuelPurchases;